"use client";

import type { HTMLAttributes } from "react";
import type {
  GalleryDevice,
  GalleryImageData,
} from "../../../blocks/image-gallery/image-gallery.types";

import { Button } from "../../../client";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../../elements/dialog/dialog";

import { GalleryImage } from "../../../elements/gallery-image/gallery-image";

import { IconArrowLeft, IconArrowRight, IconX } from "../../../icons";

import { CarouselThumbnails } from "../../../patterns/carousel-thumbnails/carousel-thumbnails";
import { useOverlayGallery } from "../../../patterns/image-gallery-overlay/use-overlay-gallery";
import { ZoomedImage } from "../../../patterns/image-gallery-overlay/zoomed-image";

// Mirrors the image container's w-[min(100vw-2rem,calc(100vh-10rem),53.5rem)];
// the height cap is left out, so short viewports only overestimate.
const OVERLAY_IMAGE_SIZES = "(min-width: 54.5rem) 53.5rem, calc(100vw - 2rem)";

export type OverlayGalleryProps = HTMLAttributes<HTMLDivElement> & {
  isVisible?: boolean;
  onClose?: () => void;

  images: GalleryImageData[];
  activeIndex: number;

  onImageChange: (index: number) => void;

  device?: GalleryDevice;

  microcopies: {
    close: string;
    previous: string;
    next: string;
    imageCounter: string;
  };
};

export function OverlayGallery({
  isVisible = true,
  onClose,
  images,
  activeIndex,
  onImageChange,
  device,
  microcopies,
}: OverlayGalleryProps) {
  const {
    isZoomed,
    currentImage,
    hasPrevious,
    hasNext,
    isVideo,
    handleCloseZoom,
    handleToggleZoom,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    handleTouchStart,
    handleTouchEnd,
  } = useOverlayGallery({
    images,
    activeIndex,
    onImageChange,
    onClose,
    initialZoomed: true,
  });

  const renderBackdropButton = () => (
    <button
      aria-hidden="true"
      className="fixed inset-0 z-0 cursor-default border-0 bg-transparent p-0"
      onClick={onClose}
      tabIndex={-1}
      type="button"
    />
  );

  const renderCloseButton = (onClick?: () => void) => (
    <Button
      aria-label={microcopies.close}
      className="absolute top-4 right-4 z-[10000]"
      onClick={onClick}
      size="icon"
      type="button"
      variant="secondary"
    >
      <IconX className="!h-4 !w-4" />
    </Button>
  );

  const renderNavigationArrows = () => (
    <div className="-translate-y-1/2 pointer-events-none absolute top-1/2 hidden w-full px-4 md:block">
      <div className="flex justify-between">
        <Button
          className="pointer-events-auto z-[10000]"
          disabled={!hasPrevious}
          onClick={handlePrevious}
          size="icon"
          type="button"
          variant="secondary"
        >
          <IconArrowLeft className="!h-4 !w-4" />
        </Button>

        <Button
          className="pointer-events-auto z-[10000]"
          disabled={!hasNext}
          onClick={handleNext}
          size="icon"
          type="button"
          variant="secondary"
        >
          <IconArrowRight className="!h-4 !w-4" />
        </Button>
      </div>
    </div>
  );

  const renderThumbnails = (
    onThumbnailClickHandler: (index: number) => void
  ) => (
    <CarouselThumbnails
      activeIndex={activeIndex}
      device={device}
      images={images}
      microcopies={{
        previous: microcopies.previous,
        next: microcopies.next,
      }}
      onThumbnailClick={onThumbnailClickHandler}
      width="Full"
    />
  );

  const renderImageContainer = (onClick: () => void) => (
    <div
      className={`pointer-events-auto relative w-[min(100vw-2rem,calc(100vh-10rem),53.5rem)] overflow-hidden rounded-lg ${
        isVideo
          ? "aspect-video bg-black"
          : "isolate aspect-square bg-whitesmoke [&_img]:mix-blend-multiply"
      }`}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
    >
      {isVideo ? (
        <iframe
          allow="autoplay; encrypted-media; picture-in-picture"
          className="h-full w-full"
          loading="eager"
          src={currentImage.videoUrl}
          title={currentImage.alt}
        />
      ) : (
        <GalleryImage
          alt={currentImage.alt}
          className={`h-full w-full select-none rounded-lg bg-transparent object-contain ${isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
          onClick={onClick}
          sizes={OVERLAY_IMAGE_SIZES}
          src={currentImage.src}
        />
      )}
    </div>
  );

  if (isZoomed && !isVideo) {
    return (
      <Dialog onOpenChange={(open) => !open && onClose?.()} open={isVisible}>
        <DialogContent galleryOverlay overlayClassName="bg-whitesmoke">
          <DialogTitle className="sr-only">
            {microcopies.imageCounter}
          </DialogTitle>

          {/* Close Button */}
          {renderCloseButton(handleCloseZoom)}

          <ZoomedImage
            className="cursor-zoom-out"
            imgAlt={currentImage.alt}
            imgSrc={currentImage.src}
            onClick={handleCloseZoom}
          />

          {/* Desktop Arrows */}
          {renderNavigationArrows()}

          <div className="-translate-x-1/2 tra absolute bottom-0 left-1/2 mb-4 select-none">
            {renderThumbnails(handleThumbnailClick)}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={(open) => !open && onClose?.()} open={isVisible}>
      <DialogContent galleryOverlay overlayClassName="bg-whitesmoke">
        <DialogTitle className="sr-only">
          {microcopies.imageCounter}
        </DialogTitle>

        {/* Backdrop - click outside the image/controls to close */}
        {renderBackdropButton()}

        {/* Close Button */}
        {renderCloseButton(onClose)}

        {/* MOBILE LAYOUT */}
        <div className="pointer-events-none flex h-screen flex-col overflow-hidden md:hidden">
          <div className="flex flex-1 items-center justify-center p-2">
            {renderImageContainer(handleToggleZoom)}
          </div>

          <div className="pointer-events-auto relative shrink-0 select-none px-4 pb-4">
            {renderThumbnails(onImageChange)}
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <div className="pointer-events-none hidden h-screen overflow-hidden p-4 md:flex md:flex-col md:items-center md:justify-center">
          {renderImageContainer(handleToggleZoom)}

          <div className="pointer-events-auto relative mt-4 shrink-0 select-none">
            {renderThumbnails(onImageChange)}
          </div>
        </div>

        {/* Desktop Arrows */}
        {renderNavigationArrows()}

        {/* Screen Reader Live Region */}
        <div aria-atomic="true" aria-live="polite" className="sr-only">
          {microcopies.imageCounter
            .replace("{current}", String(activeIndex + 1))
            .replace("{total}", String(images.length))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
